/* eslint-disable no-fallthrough */
/* eslint-disable default-case */
import type { AttributeTarget } from '@hotjar/feedback/domain/Surveys/Survey/Survey';
import {
  RadioBooleanFilterOperation,
  RadioDateFilterOperation,
  RadioNumberFilterOperation,
  RadioTextFilterOperation,
} from '@hotjar/global-filters';
import type { UserAttributeType } from '@hotjar/global-filters';
import { UserAttributeKind } from '@hotjar/webapp/domains/user-attributes/model';

export function convertFilterToTarget(filterValue: UserAttributeType): AttributeTarget {
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (filterValue.kind) {
    case UserAttributeKind.Boolean: {
      switch (filterValue.value?.operation) {
        case RadioBooleanFilterOperation.IS_TRUE:
          return {
            component: 'attribute',
            rule_type: 'boolean',
            pattern: 'true',
            match_operation: 'exact',
            name: filterValue.name ?? '',
          };
        case RadioBooleanFilterOperation.IS_FALSE:
          return {
            component: 'attribute',
            rule_type: 'boolean',
            pattern: 'false',
            match_operation: 'exact',
            name: filterValue.name ?? '',
          };
        case RadioBooleanFilterOperation.IS_UNKNOWN:
          return {
            component: 'attribute',
            rule_type: 'boolean',
            negate: false,
            match_operation: 'unknown',
            name: filterValue.name ?? '',
            pattern: true,
          };
        case RadioBooleanFilterOperation.HAS_ANY_VALUE:
          return {
            component: 'attribute',
            rule_type: 'boolean',
            negate: true,
            match_operation: 'unknown',
            name: filterValue.name ?? '',
            pattern: true,
          };
        case undefined:
          throw new Error('Unsupported case');
      }
    }
    case UserAttributeKind.Date: {
      switch (filterValue.value?.operation) {
        case RadioDateFilterOperation.HAS_ANY_VALUE:
          return {
            component: 'attribute',
            rule_type: 'date',
            negate: true,
            match_operation: 'unknown',
            name: filterValue.name ?? '',
            pattern: true,
          };
        case undefined: {
          throw new Error('Unsupported case');
        }
        case RadioDateFilterOperation.IS_EXACTLY_DAYS_AGO: {
          return {
            component: 'attribute',
            rule_type: 'date',
            pattern: filterValue.value.value,
            match_operation: 'exact_days_ago',
            name: filterValue.name ?? '',
          };
        }
        case RadioDateFilterOperation.IS_MORE_THAN_DAYS_AGO: {
          return {
            component: 'attribute',
            rule_type: 'date',
            pattern: filterValue.value.value,
            match_operation: 'more_than_days_ago',
            name: filterValue.name ?? '',
          };
        }
        case RadioDateFilterOperation.IS_LESS_THAN_DAYS_AGO: {
          return {
            component: 'attribute',
            rule_type: 'date',
            pattern: filterValue.value.value,
            match_operation: 'less_than_days_ago',
            name: filterValue.name ?? '',
          };
        }
        case RadioDateFilterOperation.IS_ON: {
          return {
            component: 'attribute',
            rule_type: 'date',
            pattern: filterValue.value.value.getTime() / 1000,
            match_operation: 'exact_date',
            name: filterValue.name ?? '',
          };
        }
        case RadioDateFilterOperation.IS_AFTER: {
          return {
            component: 'attribute',
            rule_type: 'date',
            pattern: filterValue.value.value.getTime() / 1000,
            match_operation: 'greater_than',
            name: filterValue.name ?? '',
          };
        }
        case RadioDateFilterOperation.IS_BEFORE: {
          return {
            component: 'attribute',
            rule_type: 'date',
            pattern: filterValue.value.value.getTime() / 1000,
            match_operation: 'less_than',
            name: filterValue.name ?? '',
          };
        }
        case RadioDateFilterOperation.IS_UNKNOWN: {
          return {
            component: 'attribute',
            rule_type: 'date',
            negate: false,
            match_operation: 'unknown',
            name: filterValue.name ?? '',
            pattern: true,
          };
        }
      }
    }
    case UserAttributeKind.Number: {
      switch (filterValue.value?.operation) {
        case undefined: {
          throw new Error('Unsupported case');
        }
        case RadioNumberFilterOperation.IS_GREATER_THAN: {
          return {
            component: 'attribute',
            rule_type: 'number',
            pattern: filterValue.value.value.toString(),
            match_operation: 'greater_than',
            name: filterValue.name ?? '',
          };
        }
        case RadioNumberFilterOperation.IS_LESS_THAN: {
          return {
            component: 'attribute',
            rule_type: 'number',
            pattern: filterValue.value.value.toString(),
            match_operation: 'less_than',
            name: filterValue.name ?? '',
          };
        }
        case RadioNumberFilterOperation.IS_EXACTLY: {
          return {
            component: 'attribute',
            rule_type: 'number',
            pattern: filterValue.value.value.toString(),
            match_operation: 'exact',
            negate: false,
            name: filterValue.name ?? '',
          };
        }
        case RadioNumberFilterOperation.IS_NOT: {
          return {
            component: 'attribute',
            rule_type: 'number',
            pattern: filterValue.value.value.toString(),
            match_operation: 'exact',
            negate: true,
            name: filterValue.name ?? '',
          };
        }
        case RadioNumberFilterOperation.IS_BETWEEN: {
          throw new Error('Unsupported case');
        }
        case RadioNumberFilterOperation.IS_UNKNOWN: {
          return {
            component: 'attribute',
            rule_type: 'number',
            match_operation: 'unknown',
            negate: false,
            name: filterValue.name ?? '',
            pattern: true,
          };
        }
        case RadioNumberFilterOperation.HAS_ANY_VALUE: {
          return {
            component: 'attribute',
            rule_type: 'number',
            match_operation: 'unknown',
            negate: true,
            name: filterValue.name ?? '',
            pattern: true,
          };
        }
      }
    }
    case UserAttributeKind.String: {
      switch (filterValue.value?.operation) {
        case undefined: {
          throw new Error('Unsupported case');
        }
        case RadioTextFilterOperation.CONTAINS: {
          return {
            component: 'attribute',
            rule_type: 'string',
            pattern: filterValue.value.value.toString(),
            match_operation: 'contains',
            negate: false,
            name: filterValue.name ?? '',
          };
        }
        case RadioTextFilterOperation.NOT_CONTAINS: {
          return {
            component: 'attribute',
            rule_type: 'string',
            pattern: filterValue.value.value.toString(),
            match_operation: 'contains',
            negate: true,
            name: filterValue.name ?? '',
          };
        }
        case RadioTextFilterOperation.STARTS_WITH: {
          return {
            component: 'attribute',
            rule_type: 'string',
            pattern: filterValue.value.value.toString(),
            match_operation: 'starts_with',
            name: filterValue.name ?? '',
          };
        }
        case RadioTextFilterOperation.NOT_STARTS_WITH: {
          throw new Error('Unsupported case');
        }
        case RadioTextFilterOperation.ENDS_WITH: {
          return {
            component: 'attribute',
            rule_type: 'string',
            pattern: filterValue.value.value.toString(),
            match_operation: 'ends_with',
            name: filterValue.name ?? '',
          };
        }
        case RadioTextFilterOperation.NOT_ENDS_WITH: {
          throw new Error('Unsupported case');
        }
        case RadioTextFilterOperation.IS: {
          return {
            component: 'attribute',
            rule_type: 'string',
            pattern: filterValue.value.value.toString(),
            match_operation: 'exact',
            negate: false,
            name: filterValue.name ?? '',
          };
        }
        case RadioTextFilterOperation.IS_NOT: {
          return {
            component: 'attribute',
            rule_type: 'string',
            pattern: filterValue.value.value.toString(),
            match_operation: 'exact',
            negate: true,
            name: filterValue.name ?? '',
          };
        }
        case RadioTextFilterOperation.IS_EXACTLY: {
          throw new Error('Unsupported case');
        }
        case RadioTextFilterOperation.IS_NOT_EXACTLY: {
          throw new Error('Unsupported case');
        }
        case RadioTextFilterOperation.IS_UNKNOWN: {
          return {
            component: 'attribute',
            rule_type: 'string',
            match_operation: 'unknown',
            negate: false,
            name: filterValue.name ?? '',
            pattern: true,
          };
        }
        case RadioTextFilterOperation.HAS_ANY_VALUE: {
          return {
            component: 'attribute',
            rule_type: 'string',
            match_operation: 'unknown',
            negate: true,
            name: filterValue.name ?? '',
            pattern: true,
          };
        }
        case RadioTextFilterOperation.REGEX: {
          throw new Error('Unsupported case');
        }
      }
    }
    case UserAttributeKind.Email: {
      throw new Error('Unsupported case');
    }
    case UserAttributeKind.Unknown: {
      throw new Error('Unsupported case');
    }
  }
  throw new Error('Unsupported case');
}
